import { useEffect, useRef, useState } from 'react';
import { Image } from '@sitecore-jss/sitecore-jss-nextjs';

import { isColorBright, resolveColorProperty } from '@gn-audio/jss-components';

import { CaseStudyTileProps } from 'components/CaseStudies/caseStudiesTypes';
import CaseStudyVideoComponent from 'components/CaseStudies/CaseStudyVideoComponent';

const CaseStudyTile = (caseStudyTileProps: CaseStudyTileProps) => {
  const {
    headline,
    shortDescription,
    longDescription,
    logo,
    backgroundColor,
    backgroundImage,
    link,
    linkAriaLabel,
    linkTrackingName,
    youtubeVideo,
    youkuVideo,
  } = caseStudyTileProps;

  const [isSmallGrid, setIsSmallGrid] = useState(false);
  const [isMediumGrid, setIsMediumGrid] = useState(false);
  const tileRef = useRef<HTMLAnchorElement | HTMLDivElement>(null);

  useEffect(() => {
    const checkGridSize = () => {
      if (tileRef.current) {
        const tileWidth = tileRef.current.offsetWidth;
        const tileHeight = tileRef.current.offsetHeight;
        setIsSmallGrid((prevState) => {
          const shouldBeSmall = tileWidth < 400 && tileHeight < 400;
          return prevState !== shouldBeSmall ? shouldBeSmall : prevState;
        });

        setIsMediumGrid((prevState) => {
          const shouldBeMedium = tileWidth > 400 && tileHeight < 400;
          return prevState !== shouldBeMedium ? shouldBeMedium : prevState;
        });
      }
    };

    checkGridSize();

    const resizeObserver = new ResizeObserver(() => {
      checkGridSize();
    });

    if (tileRef.current) {
      resizeObserver.observe(tileRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const bgColor = backgroundColor?.value;
  const isGradient = bgColor?.startsWith('linear-gradient') ?? false;
  const cardBackgroundColor = isGradient
    ? bgColor
    : (resolveColorProperty(bgColor) ?? 'transparent');

  const hasBackgroundImage = Boolean(backgroundImage?.value?.src);
  const isBgBright =
    !isGradient && !hasBackgroundImage ? isColorBright(cardBackgroundColor as `#${string}`) : false;

  const textColor = hasBackgroundImage ? 'white' : isBgBright ? 'black' : 'white';

  const linkHref = link?.value?.href;
  const shouldRenderLink = Boolean(linkHref);
  const hasVideo = Boolean(youtubeVideo?.value || youkuVideo?.value);

  if (!shouldRenderLink) {
    return null;
  }

  const descriptionContent = (
    <>
      {logo?.value?.src && (
        <Image
          field={logo}
          alt={logo?.value?.alt}
          loading="lazy"
          className="h-12 w-28 object-contain"
        />
      )}
      {isSmallGrid
        ? (shortDescription?.value || longDescription?.value) && (
            <p className="line-clamp-3 text-xl">
              {shortDescription?.value || longDescription?.value}
            </p>
          )
        : isMediumGrid && hasVideo
          ? (longDescription?.value || shortDescription?.value) && (
              <p className="line-clamp-1 text-xl">
                {longDescription?.value || shortDescription?.value}
              </p>
            )
          : isMediumGrid
            ? (longDescription?.value || shortDescription?.value) && (
                <p className="line-clamp-5 text-xl">
                  {longDescription?.value || shortDescription?.value}
                </p>
              )
            : (longDescription?.value || shortDescription?.value) && (
                <p className="line-clamp-[8] text-xl">
                  {longDescription?.value || shortDescription?.value}
                </p>
              )}
    </>
  );

  const content = (
    <>
      {hasBackgroundImage && (
        <>
          <div className="absolute inset-0 z-10 rounded-lg bg-gradient-to-t from-[rgba(0,0,0,0.85)] via-[rgba(0,0,0,0.85)] to-60%" />
          <Image
            field={backgroundImage}
            className="absolute bottom-0 left-0 right-0 top-0 h-full min-h-full w-full min-w-full rounded-lg object-cover"
            alt={backgroundImage?.value?.alt}
            loading="lazy"
          />
        </>
      )}
      <div className="relative z-20 flex h-full w-full flex-col justify-between p-8">
        {headline?.value && (
          <div className="ml-auto self-end rounded-full bg-black/40 px-5 py-2 text-right text-lg text-white">
            {headline.value}
          </div>
        )}

        {hasVideo && !isSmallGrid ? (
          <>
            <div className="mt-4 flex justify-center">
              <CaseStudyVideoComponent
                youtubeVideoID={youtubeVideo?.value}
                youkuVideoID={youkuVideo?.value}
              />
            </div>
            <a
              href={linkHref}
              target="_blank"
              rel="noopener noreferrer"
              {...(linkTrackingName?.value && { 'data-wt-name': linkTrackingName.value })}
              className="min-h-[30%] w-full overflow-hidden p-1 hover:no-underline focus-visible:rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            >
              {descriptionContent}
            </a>
          </>
        ) : (
          <div className="w-full overflow-hidden">{descriptionContent}</div>
        )}
      </div>
    </>
  );

  if (hasVideo) {
    return (
      <div
        ref={tileRef as React.RefObject<HTMLDivElement>}
        className="relative flex h-full w-full justify-center rounded-lg"
        {...(bgColor && {
          style: {
            background: cardBackgroundColor,
            color: textColor,
          },
        })}
      >
        {content}
      </div>
    );
  }

  return (
    <a
      ref={tileRef as React.RefObject<HTMLAnchorElement>}
      href={linkHref}
      target="_blank"
      rel="noopener noreferrer"
      {...(linkTrackingName?.value && { 'data-wt-name': linkTrackingName.value })}
      {...(linkAriaLabel?.value && { 'aria-label': linkAriaLabel.value })}
      className="relative flex h-full w-full justify-center rounded-lg hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2"
      {...(bgColor && {
        style: {
          background: cardBackgroundColor,
          color: textColor,
        },
      })}
    >
      {content}
    </a>
  );
};

export default CaseStudyTile;
